import React from "react";

/* ─── helpers ──────────────────────────────────────────────── */
function ContactLine({ personal }) {
  const parts = [personal?.email, personal?.phone, personal?.city, personal?.linkedin]
    .filter(Boolean);
  return <span className="text-xs text-gray-500 print:text-gray-600">{parts.join("  ·  ")}</span>;
}

function SectionTitle({ children }) {
  return (
    <h3 className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-500 border-b border-gray-200 pb-0.5 mb-2 mt-4 first:mt-0">
      {children}
    </h3>
  );
}

/* ─── Template 1: Modern (dark sidebar + white content) ────── */
export function TemplateModern({ data }) {
  const p = data?.personal || {};
  return (
    <div className="flex text-gray-900 bg-white min-h-[1050px] w-full text-[11px] leading-snug font-sans">
      {/* Sidebar */}
      <div className="w-[200px] shrink-0 bg-stone-900 text-stone-100 px-5 py-8 flex flex-col gap-1">
        <div className="mb-5">
          <p className="font-bold text-[15px] text-white leading-tight">{p.name || "Your Name"}</p>
          {p.headline && <p className="text-stone-400 text-[10px] mt-1 leading-snug">{p.headline}</p>}
        </div>

        {(p.email || p.phone || p.city) && (
          <div className="mb-4">
            <p className="text-[9px] font-bold uppercase tracking-widest text-amber-300 mb-1.5">Contact</p>
            {p.email && <p className="text-stone-300 text-[9px] break-all mb-1">{p.email}</p>}
            {p.phone && <p className="text-stone-300 text-[9px] mb-1">{p.phone}</p>}
            {p.city && <p className="text-stone-300 text-[9px] mb-1">{p.city}</p>}
            {p.linkedin && <p className="text-stone-400 text-[9px] break-all">{p.linkedin}</p>}
          </div>
        )}

        {data?.skills?.length > 0 && (
          <div className="mb-4">
            <p className="text-[9px] font-bold uppercase tracking-widest text-amber-300 mb-1.5">Skills</p>
            <div className="flex flex-wrap gap-1">
              {data.skills.map((s, i) => (
                <span key={i} className="text-[9px] text-stone-300 bg-stone-800 px-1.5 py-0.5 rounded">{s}</span>
              ))}
            </div>
          </div>
        )}

        {data?.certifications?.length > 0 && (
          <div>
            <p className="text-[9px] font-bold uppercase tracking-widest text-amber-300 mb-1.5">Certifications</p>
            {data.certifications.map((c, i) => (
              <p key={i} className="text-stone-300 text-[9px] mb-1">• {c}</p>
            ))}
          </div>
        )}
      </div>

      {/* Main content */}
      <div className="flex-1 px-7 py-8">
        {data?.summary && (
          <>
            <SectionTitle>Summary</SectionTitle>
            <p className="text-[10.5px] leading-relaxed text-gray-700 mb-2">{data.summary}</p>
          </>
        )}

        {data?.experience?.length > 0 && (
          <>
            <SectionTitle>Experience</SectionTitle>
            {data.experience.map((exp, i) => (
              <div key={i} className="mb-3">
                <div className="flex items-baseline justify-between">
                  <p className="font-bold text-[11px]">{exp.title} <span className="font-normal text-gray-500">·</span> {exp.company}</p>
                  <p className="text-[9px] text-gray-400 shrink-0 ml-2">{exp.start_date} – {exp.end_date || "Present"}</p>
                </div>
                {exp.location && <p className="text-[9px] text-gray-400">{exp.location}</p>}
                <ul className="mt-1 space-y-0.5">
                  {(exp.bullets || []).map((b, j) => (
                    <li key={j} className="text-[10px] text-gray-700 pl-2 before:content-['•'] before:mr-1.5 before:text-amber-500">{b}</li>
                  ))}
                </ul>
              </div>
            ))}
          </>
        )}

        {data?.projects?.length > 0 && (
          <>
            <SectionTitle>Projects</SectionTitle>
            {data.projects.map((proj, i) => (
              <div key={i} className="mb-2">
                <p className="font-bold text-[11px]">{proj.name} {proj.tech && <span className="font-normal text-gray-400 text-[9px]">({proj.tech})</span>}</p>
                {proj.description && <p className="text-[10px] text-gray-600">{proj.description}</p>}
              </div>
            ))}
          </>
        )}

        {data?.education?.length > 0 && (
          <>
            <SectionTitle>Education</SectionTitle>
            {data.education.map((edu, i) => (
              <div key={i} className="flex items-baseline justify-between mb-1">
                <p className="font-bold text-[11px]">{edu.degree} <span className="font-normal text-gray-500">·</span> {edu.institution}</p>
                {edu.year && <p className="text-[9px] text-gray-400 shrink-0 ml-2">{edu.year}</p>}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Template 2: Classic (centered header, full width) ──── */
export function TemplateClassic({ data }) {
  const p = data?.personal || {};
  return (
    <div className="bg-white min-h-[1050px] w-full px-12 py-8 text-gray-900 font-serif text-[11px] leading-snug">
      {/* Header */}
      <div className="text-center border-b-2 border-gray-800 pb-4 mb-4">
        <p className="text-[22px] font-bold tracking-wide">{p.name || "Your Name"}</p>
        {p.headline && <p className="text-[11px] text-gray-500 mt-1 italic">{p.headline}</p>}
        <p className="text-[10px] text-gray-500 mt-2">
          {[p.email, p.phone, p.city, p.linkedin].filter(Boolean).join("  ·  ")}
        </p>
      </div>

      {data?.summary && (
        <div className="mb-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-700 mb-1">Professional Summary</p>
          <p className="text-[10.5px] leading-relaxed text-gray-700">{data.summary}</p>
        </div>
      )}

      {data?.experience?.length > 0 && (
        <div className="mb-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-700 border-b border-gray-300 pb-0.5 mb-2">Experience</p>
          {data.experience.map((exp, i) => (
            <div key={i} className="mb-3">
              <div className="flex items-baseline justify-between">
                <p className="font-bold text-[11.5px]">{exp.title}</p>
                <p className="text-[9.5px] text-gray-500">{exp.start_date} – {exp.end_date || "Present"}</p>
              </div>
              <p className="text-[10px] text-gray-500 italic mb-1">{exp.company}{exp.location ? `, ${exp.location}` : ""}</p>
              <ul className="space-y-0.5 ml-3">
                {(exp.bullets || []).map((b, j) => (
                  <li key={j} className="text-[10px] text-gray-700 list-disc ml-2">{b}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {data?.education?.length > 0 && (
        <div className="mb-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-700 border-b border-gray-300 pb-0.5 mb-2">Education</p>
          {data.education.map((edu, i) => (
            <div key={i} className="flex items-baseline justify-between mb-1">
              <div>
                <p className="font-bold text-[11px]">{edu.degree}</p>
                <p className="text-[10px] text-gray-500 italic">{edu.institution}</p>
              </div>
              {(edu.year || edu.gpa) && (
                <p className="text-[9.5px] text-gray-500 text-right">{edu.year}{edu.gpa ? ` · GPA ${edu.gpa}` : ""}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {data?.skills?.length > 0 && (
        <div className="mb-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-700 border-b border-gray-300 pb-0.5 mb-2">Skills</p>
          <p className="text-[10.5px] text-gray-700">{data.skills.join("  ·  ")}</p>
        </div>
      )}

      {data?.projects?.length > 0 && (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-700 border-b border-gray-300 pb-0.5 mb-2">Projects</p>
          {data.projects.map((proj, i) => (
            <div key={i} className="mb-2">
              <p className="font-bold text-[11px]">{proj.name} {proj.tech && <span className="font-normal text-[9.5px] text-gray-400">({proj.tech})</span>}</p>
              {proj.description && <p className="text-[10px] text-gray-600">{proj.description}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Template 3: Minimal (pure ATS, clean mono) ─────────── */
export function TemplateMinimal({ data }) {
  const p = data?.personal || {};
  return (
    <div className="bg-white min-h-[1050px] w-full px-10 py-8 font-mono text-gray-900 text-[10.5px] leading-relaxed">
      <p className="text-[20px] font-bold tracking-tight">{p.name || "Your Name"}</p>
      <p className="text-[10px] text-gray-500 mt-0.5">
        {[p.email, p.phone, p.city, p.linkedin].filter(Boolean).join(" | ")}
      </p>

      {data?.summary && (
        <div className="mt-5">
          <p className="font-bold text-[11px] uppercase mb-1">Summary</p>
          <p className="text-gray-700">{data.summary}</p>
        </div>
      )}

      {data?.experience?.length > 0 && (
        <div className="mt-5">
          <p className="font-bold text-[11px] uppercase mb-2">Experience</p>
          {data.experience.map((exp, i) => (
            <div key={i} className="mb-3">
              <p className="font-bold">{exp.title}, {exp.company} | {exp.start_date} – {exp.end_date || "Present"}</p>
              {(exp.bullets || []).map((b, j) => (
                <p key={j} className="pl-3 text-gray-700">- {b}</p>
              ))}
            </div>
          ))}
        </div>
      )}

      {data?.education?.length > 0 && (
        <div className="mt-5">
          <p className="font-bold text-[11px] uppercase mb-2">Education</p>
          {data.education.map((edu, i) => (
            <p key={i}><span className="font-bold">{edu.degree}</span>, {edu.institution}{edu.year ? ` | ${edu.year}` : ""}{edu.gpa ? ` | GPA: ${edu.gpa}` : ""}</p>
          ))}
        </div>
      )}

      {data?.skills?.length > 0 && (
        <div className="mt-5">
          <p className="font-bold text-[11px] uppercase mb-1">Skills</p>
          <p className="text-gray-700">{data.skills.join(", ")}</p>
        </div>
      )}

      {data?.certifications?.length > 0 && (
        <div className="mt-5">
          <p className="font-bold text-[11px] uppercase mb-1">Certifications</p>
          {data.certifications.map((c, i) => <p key={i} className="text-gray-700">- {c}</p>)}
        </div>
      )}

      {data?.projects?.length > 0 && (
        <div className="mt-5">
          <p className="font-bold text-[11px] uppercase mb-2">Projects</p>
          {data.projects.map((proj, i) => (
            <div key={i} className="mb-2">
              <p><span className="font-bold">{proj.name}</span>{proj.tech ? ` (${proj.tech})` : ""}</p>
              {proj.description && <p className="text-gray-600 pl-2">{proj.description}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export const TEMPLATES = [
  { id: "modern", label: "Modern", desc: "Dark sidebar · Skills chips · Accent colour", Component: TemplateModern },
  { id: "classic", label: "Classic", desc: "Centered header · Serif · Traditional", Component: TemplateClassic },
  { id: "minimal", label: "Minimal", desc: "Pure ATS · Monospace · Maximum scanability", Component: TemplateMinimal },
];
